import { useEffect, useRef, useState } from 'react';
import './ResultsPage.css';

const BRAD_PHOTO = 'https://customer-assets.emergentagent.com/job_5e28a1ef-70e1-44d5-be9a-2c7939386bf2/artifacts/ectdlgir_brad-hero.jpg';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_only-creator/artifacts/90vlzise_original%20color%20logo%20%281%29.png';
const AUDIT_URL = 'https://findyouronly.app/audit';

export default function ResultsPage({
  userData,
  result,
  linkedinShareCount,
  onCopyStatement,
  onDownloadPDF,
  onShareLinkedIn,
  onSaveIGStory,
  igStoryRendering,
  onAuditClick,
}) {
  const navRef = useRef(null);
  const contentRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.body.classList.add('results-active');
    // Override html/body overflow-x:hidden (set globally in App.css) which would
    // otherwise establish a scroll container and break position:sticky on the photo.
    const prevHtmlOverflowX = document.documentElement.style.overflowX;
    const prevBodyOverflowX = document.body.style.overflowX;
    document.documentElement.style.overflowX = 'clip';
    document.body.style.overflowX = 'clip';
    const raf = requestAnimationFrame(() => document.body.classList.add('rp-lit'));
    return () => {
      document.body.classList.remove('results-active');
      document.body.classList.remove('rp-lit');
      document.documentElement.style.overflowX = prevHtmlOverflowX;
      document.body.style.overflowX = prevBodyOverflowX;
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll('.results-page .rp-reveal');
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('on'); io.unobserve(e.target); }
      }),
      { threshold: 0.06, rootMargin: '0px 0px -28px 0px' }
    );
    els.forEach((el) => io.observe(el));
    const timeout = setTimeout(() => {
      document.querySelectorAll('.results-page .rp-reveal:not(.on)').forEach((el) => el.classList.add('on'));
    }, 250);
    return () => { io.disconnect(); clearTimeout(timeout); };
  }, []);

  const fn = (userData && userData.firstName) || 'Friend';
  const obs = (result && result.observations) || [];
  const showShareProof = linkedinShareCount >= 10;

  return (
    <div className="results-page" data-testid="results-screen">
      <div className="rp-grain" aria-hidden="true"></div>
      <div className="rp-layout">

        {/* LEFT — sticky photo */}
        <aside className="rp-photo" aria-hidden="true">
          <img src={BRAD_PHOTO} alt="Brad Holcman on stage" />
          <div className="rp-photo-veil"></div>
          <div className="rp-divider"></div>
          <div className="rp-floatcred">
            <span className="dot"></span>
            <div className="nm">Brad Holcman</div>
            <div className="rl">Creator of the ONLY Framework</div>
            <div className="ru"></div>
            <div className="mt">22 Year Hollywood TV Exec · 4× Emmy Nominated · $2 billion in content · 100+ hit series</div>
          </div>
        </aside>

        {/* RIGHT — content */}
        <section className="rp-content" ref={contentRef}>
          <nav ref={navRef} className={`rp-nav${scrolled ? ' scrolled' : ''}`}>
            <img src={LOGO_URL} alt="$2 Bill Brand" />
            <a href={AUDIT_URL} target="_blank" rel="noopener noreferrer" className="rp-nav-cta"
               onClick={() => onAuditClick && onAuditClick('results_nav')} data-testid="rp-nav-cta">
              Book Your Audit
            </a>
          </nav>

          <div className="rp-inner">

            {/* HERO HEADING */}
            <header className="rp-hero rp-reveal">
              <div className="rp-eyebrow">Your Personal Branding Report</div>
              <h1 className="rp-h1" data-testid="results-headline">
                <span className="nm">{fn}</span>, here's what we found.
              </h1>
              <p className="rp-sub">Generated from your answers · {new Date().toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' })}</p>
            </header>

            {/* STATEMENT */}
            <div className="rp-stmt rp-reveal" style={{ '--rd': '.05s' }} data-testid="statement-card">
              <div className="rp-stmt-inner">
                {result.category_name && (
                  <div className="rp-category" data-testid="category-name">{result.category_name}</div>
                )}
                <div className="rp-lbl">Your ONLY Statement</div>
                <p className="rp-stmt-text">"{result.statement}"</p>
                <p className="rp-stmt-note" data-testid="statement-subtext">
                  This is your draft. It's pointed in the right direction. Here's what we're seeing underneath it.
                </p>
                <div className="rp-actions" data-testid="statement-inline-actions">
                  <button className="rp-ghost" onClick={onCopyStatement} data-testid="inline-copy-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    Copy
                  </button>
                  <button className="rp-ghost" onClick={onDownloadPDF} data-testid="inline-download-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download PDF
                  </button>
                  <button className="rp-ghost" onClick={onShareLinkedIn} data-testid="inline-share-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 11.001-4.124 2.062 2.062 0 01-.001 4.124zm1.782 13.019H3.555V9h3.564v11.452z"/></svg>
                    Share
                  </button>
                  <button
                    className="rp-ghost"
                    onClick={onSaveIGStory}
                    disabled={!!igStoryRendering}
                    data-testid="inline-ig-story-btn"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4.2" />
                      <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
                    </svg>
                    {igStoryRendering ? 'Saving…' : 'IG Story'}
                  </button>
                </div>
                {showShareProof && (
                  <p className="rp-share-proof" data-testid="linkedin-share-proof">
                    {linkedinShareCount.toLocaleString()} {linkedinShareCount === 1 ? 'person' : 'people'} shared this on LinkedIn this week
                  </p>
                )}
                <a className="rp-cta rp-cta-inline" href={AUDIT_URL} target="_blank" rel="noopener noreferrer"
                   onClick={() => onAuditClick && onAuditClick('statement_inline')}
                   data-testid="audit-cta-statement-inline">
                  Book Your ONLY Statement Audit
                </a>
              </div>
            </div>

            {/* WHAT WE'RE SEEING */}
            {obs.length > 0 && (
              <div className="rp-card rp-reveal" style={{ '--rd': '.08s' }} data-testid="whats-seeing-section">
                <div className="rp-card-in">
                  <h2 className="rp-card-title">What We're <em>Seeing</em></h2>
                  <div className="rp-obs-list" data-testid="observations-list">
                    {obs.map((o, i) => (
                      <div className="rp-obs-item" key={i} data-testid={`observation-${i + 1}`}>
                        <span className="rp-obs-num">{String(i + 1).padStart(2,'0')}</span>
                        <p>{o}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* BLURRY */}
            {result.whats_blurry && (
              <div className="rp-card rp-reveal" style={{ '--rd': '.1s' }} data-testid="whats-blurry-section">
                <div className="rp-card-in">
                  <h2 className="rp-card-title">What's Still <em>Blurry</em></h2>
                  <div className="rp-blurry-rule"></div>
                  <p className="rp-blurry-text" data-testid="whats-blurry-text">{result.whats_blurry}</p>
                </div>
              </div>
            )}

            {/* POSITIONING PATTERNS — one card per pattern, no numbered badges */}
            {Array.isArray(result.positioning_patterns) && result.positioning_patterns.length > 0 && (
              <div className="rp-patterns-block rp-reveal" style={{ '--rd': '.11s' }} data-testid="positioning-patterns-section">
                <div className="rp-card">
                  <div className="rp-card-in">
                    <h2 className="rp-card-title">Your Positioning <em>Patterns</em></h2>
                    <p className="rp-blurry-text" style={{ marginBottom: 18 }} data-testid="positioning-patterns-intro">
                      Across your answers, here are the threads that kept showing up:
                    </p>
                  </div>
                </div>
                {result.positioning_patterns.map((p, i) => (
                  <div
                    className="rp-card"
                    key={`pp-${i}`}
                    style={{ marginTop: 14 }}
                    data-testid={`positioning-pattern-${i + 1}`}
                  >
                    <div className="rp-card-in">
                      <p className="rp-blurry-text" style={{ margin: 0 }}>{p}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* VERSIONS */}
            <div className="rp-card rp-reveal" style={{ '--rd': '.12s' }} data-testid="variations-section">
              <div className="rp-card-in">
                <h2 className="rp-card-title">Three directions your statement <em>could go</em></h2>
                <div className="rp-versions">
                  <div className="rp-version primary" data-testid="variation-1">
                    <div className="rp-vhead">
                      <span className="rp-vtag">Version 1</span>
                      <span className="rp-pbadge">Primary</span>
                    </div>
                    <div className="rp-vbody"><p className="rp-vtext">"{result.statement}"</p></div>
                  </div>
                  {result.statement_option_2 && (
                    <div className="rp-version alt" data-testid="variation-2">
                      <div className="rp-vhead"><span className="rp-vtag">Version 2</span></div>
                      <div className="rp-vbody"><p className="rp-vtext">"{result.statement_option_2}"</p></div>
                    </div>
                  )}
                  {result.statement_option_3 && (
                    <div className="rp-version alt" data-testid="variation-3">
                      <div className="rp-vhead"><span className="rp-vtag">Version 3</span></div>
                      <div className="rp-vbody"><p className="rp-vtext">"{result.statement_option_3}"</p></div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* TESTIMONIAL */}
            <div className="rp-testi rp-reveal" style={{ '--rd': '.08s' }} data-testid="testimonial-sharon-mid">
              <span className="rp-testi-qm" aria-hidden="true">&ldquo;</span>
              <p className="rp-testi-q">
                My session with Brad was emotionally changing for me. I have been doubting what my gift is for working with people. He helped me see that I do have something of value to share with others. I felt seen and heard.
              </p>
              <div className="rp-testi-rule"></div>
              <div className="rp-testi-nm">Sharon Thornburg</div>
              <div className="rp-testi-rl">Coach</div>
            </div>

            {/* BRAD BAND */}
            <div className="rp-bband rp-reveal" style={{ '--rd': '.06s' }} data-testid="brad-authority-card">
              <img className="rp-bp" src={BRAD_PHOTO} alt="Brad Holcman" data-testid="brad-authority-photo" />
              <div>
                <div className="rp-bn" data-testid="brad-authority-name">Brad Holcman</div>
                <div className="rp-bc" data-testid="brad-authority-credential">
                  4× Emmy-Nominated TV Executive · 22 years · $2B in content · 100+ hit series
                </div>
              </div>
            </div>

            <div className="rp-secdiv rp-reveal" style={{ '--rd': '.04s' }}></div>

            {/* AUDIT OFFER */}
            <div className="rp-audit rp-reveal" style={{ '--rd': '.06s' }} data-testid="audit-pitch-section">
              <div className="rp-audit-in">
                <h2 className="rp-audit-h">The draft is the beginning. <em>The audit is where we finish it.</em></h2>
                <p className="rp-audit-body">
                  In 60 minutes, I ask the questions the quiz cannot ask. I listen for the thing you said in passing that turns out to be the whole point. I find the pattern you have been living your entire career but never consciously named.
                </p>
                <p className="rp-audit-body">
                  Within 48 hours, you get a 15-17 page positioning PDF: your refined ONLY Statement, five variations, your brand thread, your ideal client profile, your 60-second elevator pitch, platform headlines for LinkedIn and your website bio, competitive differentiation, and a 90-day plan.
                </p>
                <div className="rp-pullquote">
                  "People told me afterward: 'I hired brand strategists for $5,000 who did not get me to this sentence in a year.'"
                </div>
                <div className="rp-wyg-title">What You Get</div>
                <ul className="rp-wyg-list" data-testid="audit-pitch-bullets">
                  {[
                    '60-minute 1-on-1 working session with Brad',
                    '15-17 page positioning PDF delivered within 48 hours',
                    'Refined ONLY Statement plus five variations',
                    'Brand thread, ideal client profile, elevator pitch',
                    'LinkedIn headline, website bio, cold outreach opener',
                    '90-day positioning plan',
                  ].map((it, i) => (
                    <li className="rp-wyg-item" key={i}>
                      <span className="rp-wyg-dot" style={{ animationDelay: `${i * 0.4}s` }}></span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <p className="rp-slots" data-testid="audit-pitch-urgency">
                  4-7 sessions available per week. Most people are booked within 7-10 days of placing their order.
                </p>
                <a className="rp-cta" href={AUDIT_URL} target="_blank" rel="noopener noreferrer"
                   onClick={() => onAuditClick && onAuditClick('audit_pitch_section_primary')}
                   data-testid="audit-cta-primary">
                  See What's Next For Your Positioning
                </a>
              </div>
            </div>

            {/* WHY $797 */}
            <div className="rp-why rp-reveal" style={{ '--rd': '.08s' }} data-testid="why-pricing-section">
              <p className="rp-why-title">Why <em>$797?</em></p>
              <p>
                You are getting 60 minutes of the positioning instinct that greenlit over $2 billion in content, applied entirely to one sentence about you. The audit is either the most valuable 60 minutes you spend on your brand this year, or the first chapter of something bigger.
              </p>
            </div>

            {/* FINAL CTA */}
            <div className="rp-final rp-reveal" style={{ '--rd': '.1s' }}>
              <a className="rp-cta" href={AUDIT_URL} target="_blank" rel="noopener noreferrer"
                 onClick={() => onAuditClick && onAuditClick('results_bottom')}
                 data-testid="audit-cta-bottom">
                Book Your ONLY Statement Audit
              </a>
              <p className="rp-final-note" data-testid="not-ready-footnote">
                Not ready yet? Email us at <a href="mailto:info@findyouronly.app">info@findyouronly.app</a> with any questions.
              </p>
            </div>

            {/* FOOTER */}
            <div className="rp-foot">
              <img src={LOGO_URL} alt="$2 Bill Brand" />
              <div className="rp-fsoc">
                <a href="https://www.youtube.com/@BradleyHolcman" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="rp-footer-youtube"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.5 15.5v-7l6.3 3.5-6.3 3.5z"/></svg></a>
                <a href="https://www.facebook.com/BradHolcman" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="rp-footer-facebook"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12a12 12 0 1 0-13.9 11.9v-8.4H7.1V12h3V9.4c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.4l-.5 3.5h-2.9v8.4A12 12 0 0 0 24 12z"/></svg></a>
                <a href="https://www.linkedin.com/in/brad-holcman/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="rp-footer-linkedin"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5V9h3v10zM6.5 7.7a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4zM19 19h-3v-5c0-1.2 0-2.7-1.6-2.7S12.5 12.5 12.5 14v5h-3V9h2.9v1.4h.04A3.2 3.2 0 0 1 15.3 8.7c3.1 0 3.7 2 3.7 4.7V19z"/></svg></a>
                <a href="https://www.tiktok.com/@bholcman" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="rp-footer-tiktok"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.6 6.7a4.8 4.8 0 0 1-3.3-2.2A4.8 4.8 0 0 1 15.5 2h-3.3v13.4a2.7 2.7 0 1 1-2.7-2.7c.3 0 .5 0 .8.1v-3.4a6 6 0 0 0-.8-.1 6.1 6.1 0 1 0 6.1 6.1V9.1a8 8 0 0 0 4 1.1V6.7h-.04z"/></svg></a>
                <a href="https://www.instagram.com/bradneedsabs/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="rp-footer-instagram"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4.5"/><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/></svg></a>
              </div>
              <p className="rp-fcopy">findyouronly.app · The $2 Bill Brand · All Rights Reserved</p>
            </div>

          </div>
        </section>
      </div>
    </div>
  );
}
